import { ComponentIds, Entity } from '@inter-ikea-core/core';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { Product, ProductInfoComponent, ProductType } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { getMarketProducts } from 'ducks/products';
import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Divider from 'ui/Divider/Divider';
import Body from 'ui/Typography/Body';
import Title from 'ui/Typography/Title';
import media from 'ui/media';
import { adjustMeasurementsSystem } from 'utils/adjustMeasurementsSystem';
import doorIcon from './Door.png';
import { useTranslations } from 'hooks/useTranslations';
import { IStorageOneTranslations, useServiceSettings } from 'contexts/serviceSettings';

const DoorIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-right: ${({ theme }) => theme.space.s};
  margin-top: ${({ theme }) => theme.space.s};
  ${media.mobile`
    margin-right: 0;
    margin-top: 0;
  `}
`;

const Container = styled.div`
  margin: 0 0 ${({ theme }) => theme.space.xl} 0;
  padding: ${({ theme }) => theme.space.s} 0;
  display: flex;
  flex-direction: column;
  ${media.mobile`
    display: block;
  `}
`;

const HeaderStyled = styled.div`
  width: 100%;
  display: flex;
  align-items: start; /* Updated alignment */
  margin: 0 0 ${({ theme }) => theme.space.s} 0;
  ${media.mobile`
    display: block;
  `}
`;

const HeaderDescription = styled(Body)`
  color: inherit;
`;

const PlatsaSoftClosingAssemblyInstruction = () => {
  const coreApi = useCore();
  const { formatMessage } = useIntl();
  const { useMetric } = useTranslations();
  const { translations } = useServiceSettings();
  const allProducts = useSelector(getMarketProducts);
  const doorProducts = useMemo(
    () => allProducts.filter(p => [ProductType.PLATSA_SC_HINGE_DOOR_ASM].includes(p.type)),
    [allProducts]
  );

  const doorsInScene = useMemo(
    () => coreApi.entities.getByComponent(ComponentIds.DOOR_COMPONENT_ID),
    [coreApi.entities]
  );

  const productType = ProductType.PLATSA_SC_HINGE_DOOR_ASM;
  const [doorsInSummary, setDoorsInSummary] = React.useState<Product[]>([]);

  const getStringMeasurements = (
    measurements: { width: number; height: number },
    translations: IStorageOneTranslations,
    useMetricMeasures?: boolean
  ) => {
    const width = adjustMeasurementsSystem(measurements.width, translations, useMetricMeasures);
    const height = adjustMeasurementsSystem(measurements.height, translations, useMetricMeasures);

    return `${width}x${height}`;
  };

  useEffect(() => {
    const uniqueSizesAndHeights = new Set(); // Track unique size and height combinations
    const filteredDoors: Product[] = doorsInScene.reduce((filtered: Product[], door: Entity) => {
      const size = coreApi.entities.getEntityComSize(door);
      const info = door.getComponent<ProductInfoComponent>(ProductInfoComponent.id);
      if (info?.productType === productType) {
        const sizeKey = `${size?.width}-${size?.height}`; // Create a unique key for size and height
        // Check if this size and height combination is already encountered
        if (!uniqueSizesAndHeights.has(sizeKey)) {
          uniqueSizesAndHeights.add(sizeKey); // Add to unique set
          const matchingProduct = doorProducts.find(
            p =>
              p.communicatedDimensions.width === size?.width &&
              p.communicatedDimensions.height === size?.height &&
              p.name === info?.productName &&
              p.design === info?.design &&
              p.type === productType
          );
          if (matchingProduct) {
            return [...filtered, matchingProduct];
          }
        }
      }
      return filtered;
    }, []);
    setDoorsInSummary(filteredDoors);
  }, [doorProducts, doorsInScene, coreApi.entities, productType]);
  return (
    <>
      {doorsInSummary.map(p => (
        <div key={p.coreProductId}>
          <Divider />
          <Container>
            <HeaderStyled>
              <DoorIcon src={doorIcon} alt="Door icon" />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Title type={ThemeFontStyleTypeEnum.headingMedium} pb="xxs" mx="xxs">
                  {`${formatMessage({ id: 'storageone.doorName' })}  ${getStringMeasurements(
                    {
                      width: p.communicatedDimensions.width,
                      height: p.communicatedDimensions.height
                    },
                    translations,
                    useMetric
                  )} ${formatMessage({ id: 'kompis.measureValue' })}`}
                </Title>
                {p.goodToKnow[2].text && (
                  <HeaderDescription type={ThemeFontStyleTypeEnum.bodyMedium}>
                    {p.goodToKnow[2].text}
                  </HeaderDescription>
                )}
              </div>
            </HeaderStyled>
          </Container>
        </div>
      ))}
    </>
  );
};

export default PlatsaSoftClosingAssemblyInstruction;
